"use client";
/**
 * Referrals page (Block 5, design 1.9): hero, code + share + QR, "how it
 * works", stats, and the referral history list. Includes an empty state (no
 * referrals yet) and a QR share modal (real QR via the `qrcode` dep).
 *
 * Hidden by routing through `useReferral().enabled` — when referrals are off
 * (or the endpoint 404s) we show a graceful "not available" state.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Button, Card, TopBar } from "@/components/ui";
import { BottomSheet, SignedOutGate } from "@/components/loyalty/LoyaltyBits";
import { QrCode } from "@/components/loyalty/QrCode";
import { useReferral } from "@/hooks/useLoyaltyExpansion";
import type { ReferralHistoryEntry } from "@/api/services";
import { useAuth } from "@/store/auth";

function avatarColors(name: string): [string, string] {
  const hues: [string, string][] = [
    ["#fde9c7", "#9a5b15"],
    ["#fbe2dc", "#a73e2b"],
    ["#dbeadf", "#3a7b54"],
    ["#f7d8cf", "#a73e2b"],
    ["#c4ebd7", "#08793e"],
  ];
  const idx = (name.charCodeAt(0) || 0) % hues.length;
  return hues[idx];
}

function Avatar({ name }: { name: string }) {
  const initials =
    name
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("") || "?";
  const [bg, fg] = avatarColors(name);
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 999,
        background: bg,
        color: fg,
        display: "grid",
        placeItems: "center",
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
      }}
    >
      {initials.toUpperCase()}
    </div>
  );
}

export function ReferralsScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const { isLoggedIn } = useAuth();
  const { loading, error, referral, enabled, unavailable, refresh } = useReferral();
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const code = referral?.code ?? "";
  const link = referral?.link ?? code;

  const copyCode = async () => {
    try {
      await navigator.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  const shareLink = async () => {
    const text = t("v2.referrals.share_text", { code });
    try {
      if (navigator.share) {
        await navigator.share({ title: t("v2.referrals.title"), text, url: link });
      } else {
        await navigator.clipboard?.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      /* cancelled — no-op */
    }
  };

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.referrals.title")} onBack={() => navigate("/profile")} />
        <SignedOutGate
          icon={<Icons.share size={32} />}
          title={t("v2.referrals.signin_title")}
          desc={t("v2.referrals.signin_desc")}
          cta={t("v2.profile.signin_phone")}
        />
      </div>
    );
  }

  if (!loading && (unavailable || !enabled)) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.referrals.title")} onBack={() => navigate("/profile")} />
        <div style={{ padding: "42px 24px", textAlign: "center", color: "var(--ink-2)" }}>
          <div
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              color: "var(--ink-3)",
              margin: "0 auto 14px",
            }}
          >
            <Icons.share size={32} />
          </div>
          <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.referrals.unavailable_title")}
          </div>
          <div style={{ maxWidth: 280, margin: "8px auto 0", fontSize: 13, lineHeight: 1.45 }}>
            {t("v2.referrals.unavailable_desc")}
          </div>
        </div>
      </div>
    );
  }

  const history = referral?.history ?? [];
  const stats = referral?.stats ?? {};
  const isEmpty = history.length === 0;

  const TwoPeopleGlyph = () => (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: 18,
        background: "var(--primary-600)",
        color: "#fff",
        display: "grid",
        placeItems: "center",
        margin: "0 auto",
        boxShadow: "0 12px 24px -8px rgba(11,170,87,0.4)",
        position: "relative",
        zIndex: 1,
      }}
    >
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="11" r="4.5" />
        <circle cx="24" cy="11" r="4.5" />
        <path d="M3 30c1-4.5 4.5-7 9-7s8 2.5 9 7" />
        <path d="M15 30c1-4.5 4.5-7 9-7s8 2.5 9 7" />
      </svg>
    </div>
  );

  const CodeBox = () => (
    <Card
      padding={0}
      style={{
        display: "flex",
        alignItems: "stretch",
        border: "1.5px dashed var(--border-strong)",
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      <div style={{ flex: 1, padding: "14px 16px", display: "flex", alignItems: "center", minWidth: 0 }}>
        <span
          className="display num"
          dir="ltr"
          style={{
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "0.06em",
            fontFamily: "var(--font-mono)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {code || "—"}
        </span>
      </div>
      <button
        onClick={copyCode}
        style={{
          padding: "0 18px",
          background: "var(--surface-2)",
          border: 0,
          borderInlineStart: "1px dashed var(--border-strong)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--primary-700)",
          fontWeight: 700,
          fontSize: 13,
          fontFamily: "inherit",
        }}
      >
        <Icons.copy size={15} /> {copied ? t("v2.referrals.copied") : t("v2.referrals.copy")}
      </button>
    </Card>
  );

  const ShareButtons = () => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
      <Button onClick={shareLink} leading={<Icons.share size={16} />}>
        {t("v2.referrals.share_link")}
      </Button>
      <Button variant="outline" onClick={() => setQrOpen(true)} leading={<Icons.qr size={16} />}>
        {t("v2.referrals.show_qr")}
      </Button>
    </div>
  );

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={t("v2.referrals.title")}
        onBack={() => navigate("/profile")}
        right={
          <Button variant="ghost" size="sm" onClick={() => refresh()} disabled={loading}>
            {t("v2.common.retry")}
          </Button>
        }
      />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Hero */}
        <Card
          padding={0}
          style={{
            padding: "22px 18px 18px",
            background: "linear-gradient(160deg, var(--surface-1) 0%, var(--primary-50) 100%)",
            border: "1px solid var(--primary-100)",
            boxShadow: "none",
            position: "relative",
            overflow: "hidden",
            textAlign: "center",
          }}
        >
          <TwoPeopleGlyph />
          <h1 className="display" style={{ fontSize: 22, marginTop: 14, marginBottom: 0, letterSpacing: "-0.025em" }}>
            {isEmpty ? t("v2.referrals.hero_empty_title") : t("v2.referrals.hero_title")}
          </h1>
          <div className="muted" style={{ fontSize: 13.5, marginTop: 8, padding: "0 6px", lineHeight: 1.5 }}>
            {isEmpty ? t("v2.referrals.hero_empty_desc") : t("v2.referrals.hero_desc")}
          </div>
        </Card>

        {error && (
          <Card padding={14} style={{ borderColor: "#fecaca", background: "#fff7f7" }}>
            <div style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}>
              {t("v2.loyalty.couldnt_load")}
            </div>
          </Card>
        )}

        {/* Code + share */}
        <div>
          <div
            className="display"
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--ink-3)",
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {t("v2.referrals.your_code")}
          </div>
          <CodeBox />
          <ShareButtons />
        </div>

        {/* How it works */}
        <section>
          <div
            className="display"
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--ink-2)",
              marginBottom: 10,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {t("v2.referrals.how_it_works")}
          </div>
          <Card padding={0}>
            {[1, 2, 3].map((n, i) => (
              <div
                key={n}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  borderTop: i === 0 ? 0 : "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    background: "var(--primary-50)",
                    color: "var(--primary-700)",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 800,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {n}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                    {t(`v2.referrals.step${n}_title`)}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 1 }}>
                    {t(`v2.referrals.step${n}_sub`)}
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </section>

        {/* Stats + history */}
        {isEmpty ? (
          <div style={{ padding: "20px 16px 0", textAlign: "center" }}>
            <div className="muted" style={{ fontSize: 13 }}>
              {t("v2.referrals.no_referrals")}
            </div>
          </div>
        ) : (
          <section>
            <div className="display" style={{ fontSize: 16, marginBottom: 10 }}>
              {t("v2.referrals.your_referrals")}
            </div>
            <Card padding={14} style={{ marginBottom: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4 }}>
                <Stat n={String(stats.invited ?? history.length)} l={t("v2.referrals.stat_joined")} />
                <Stat n={String(stats.attributed ?? 0)} l={t("v2.referrals.stat_completed")} />
                <Stat n={String(stats.rewardsEarned ?? 0)} l={t("v2.referrals.stat_earned")} accent />
              </div>
            </Card>
            <Card padding={0}>
              {history.map((r, i) => (
                <ReferralRow key={i} entry={r} index={i} lang={lang} t={t} />
              ))}
            </Card>
          </section>
        )}
      </div>

      {/* QR share modal */}
      <BottomSheet open={qrOpen} onClose={() => setQrOpen(false)} label={t("v2.referrals.show_qr")}>
        <div style={{ textAlign: "center" }}>
          <div className="display" style={{ fontSize: 17 }}>
            {t("v2.referrals.qr_title")}
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {t("v2.referrals.qr_sub")}
          </div>
        </div>
        <div
          style={{
            marginTop: 14,
            padding: "20px 16px",
            borderRadius: 18,
            background: "linear-gradient(160deg, var(--surface-1), var(--primary-50))",
            textAlign: "center",
          }}
        >
          <div
            style={{
              display: "inline-block",
              padding: 12,
              borderRadius: 16,
              background: "#fff",
              boxShadow: "0 8px 24px -8px rgba(0,0,0,0.12)",
            }}
          >
            <QrCode value={link} size={170} />
          </div>
          <div
            className="display num"
            dir="ltr"
            style={{
              marginTop: 14,
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: "0.08em",
              fontFamily: "var(--font-mono)",
            }}
          >
            {code}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
          <Button variant="outline" onClick={shareLink} leading={<Icons.share size={16} />}>
            {t("v2.referrals.share_link")}
          </Button>
          <Button onClick={copyCode} leading={<Icons.copy size={16} />}>
            {copied ? t("v2.referrals.copied") : t("v2.referrals.copy_code")}
          </Button>
        </div>
      </BottomSheet>
    </div>
  );
}

function Stat({ n, l, accent }: { n: string; l: string; accent?: boolean }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        className="num display"
        style={{ fontSize: 19, fontWeight: 800, color: accent ? "var(--primary-700)" : "var(--ink-1)" }}
      >
        {n}
      </div>
      <div
        className="subtle"
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginTop: 2,
        }}
      >
        {l}
      </div>
    </div>
  );
}

function ReferralRow({
  entry,
  index,
  lang,
  t,
}: {
  entry: ReferralHistoryEntry;
  index: number;
  lang: string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const name = entry.refereeName || entry.name || t("v2.referrals.a_friend");
  const joinedAt = entry.joinedAt || entry.createdAt;
  const status = (entry.status ?? "pending").toLowerCase();
  const isEarned = status === "rewarded" || status === "earned";
  const isExpired = status === "expired" || status === "flagged_self_referral";
  const tone = isEarned ? "success" : isExpired ? "" : "warn";
  const note = isEarned
    ? t("v2.referrals.status_earned")
    : isExpired
      ? t("v2.referrals.status_expired")
      : t("v2.referrals.status_pending");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: 14,
        borderTop: index === 0 ? 0 : "1px solid var(--border)",
      }}
    >
      <Avatar name={name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{name}</div>
        {joinedAt && (
          <div className="subtle" style={{ fontSize: 11.5, marginTop: 1 }}>
            {t("v2.referrals.joined_on", { date: new Date(joinedAt).toLocaleDateString(lang) })}
          </div>
        )}
      </div>
      <span className={"badge " + tone} style={{ flexShrink: 0 }}>
        {isEarned && <Icons.check size={11} />}
        {note}
      </span>
    </div>
  );
}
